import { SigninSilentArgs, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

export const oidcConfiguration: UserManagerSettings = {
  authority: process.env.REACT_APP_API_AUTHORITY || '',
  client_id: process.env.REACT_APP_API_CLIENT_ID || '',
  redirect_uri: process.env.REACT_APP_API_REDIRECT_URL || '/callback',
  silent_redirect_uri: `${process.env.REACT_APP_API_SILENT_REDIRECT_URL}` || '',
  post_logout_redirect_uri: process.env.REACT_APP_API_LOGOUT_REDIRECT_URL || '',
  scope: process.env.REACT_APP_API_SCOPE,
  response_type: 'code',
  // monitorSession: true,
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  userStore: new WebStorageStateStore({ store: window.localStorage })
};

export const userManager = new UserManager(oidcConfiguration);

export const userKey = `oidc.user:${oidcConfiguration.authority}:${oidcConfiguration.client_id}`;

const originalSigninSilent = userManager.signinSilent.bind(userManager);
let inMemoryRenewPromise: Promise<User | null> | null = null;

userManager.signinSilent = async (args?: SigninSilentArgs): Promise<User | null> => {
  // 1. In-tab deduplication (between 60s background renew timer and 401 API retries)
  if (inMemoryRenewPromise) return inMemoryRenewPromise;

  inMemoryRenewPromise = (async () => {
    try {
      // Fallback if browser does not support Web Locks API
      if (typeof navigator === 'undefined' || !('locks' in navigator)) return await originalSigninSilent(args);

      // 2. Cross-tab synchronization using Web Locks API
      return await navigator.locks.request('oidc_token_silent_renew_lock', async () => {
        // Check if another tab has already completed the token renewal
        const currentUser = await userManager.getUser();
        const expiringThreshold = oidcConfiguration.accessTokenExpiringNotificationTimeInSeconds ?? 60;

        // If the token in storage is fresh (> 60s remaining), another tab already renewed it
        if (currentUser && currentUser.expires_in && currentUser.expires_in > expiringThreshold) {
          userManager.events.load(currentUser);
          return currentUser;
        }

        // No tab has renewed it yet -> proceed with Auth Server request
        return originalSigninSilent(args);
      });
    } finally {
      inMemoryRenewPromise = null;
    }
  })();

  return inMemoryRenewPromise;
};

// Listen to storage events: when one tab saves a refreshed user to localStorage,
// other tabs automatically sync their user state and renew timers without hitting Auth Server
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== userKey || !event.newValue) return;
    try {
      const user = User.fromStorageString(event.newValue);
      userManager.events.load(user);
    } catch (e) {
      console.warn('[OIDC] Failed to sync user across tabs:', e);
    }
  });
}
